import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { RootState } from "../../redux/Store";
import { advanceSearchCaseList } from "../../services/CaseInformationApi";
import useBreadcrumbs from "../../hooks/useBreadCrumbs";
import DataTable from "../../common/DataTable";
import CustomPagination from "../../common/Pagination";
import TableRowsPerPage from "../../common/TableRowsPerPage";
import { SEARCHCASE_HEADERS } from "../dashboard/actions/Constant";
import Loader from "../../common/Loader";
import { setIsSearchEditable } from "../../redux/slice/CaseHeaderSlice";
import AccordionComponent from "../../common/Accordion"; // Make sure this exists and is imported
import CreateNewCase from "../Case_Management/Case_Information"; // If needed
import {
  setIsDashboard,
  setNewCaseInFoID,
} from "../../redux/slice/CaseHeaderSlice";

const AdvanceSearchCase: React.FC = () => {
  const breadcrumbs = useBreadcrumbs();
  const dispatch = useDispatch();
  const loggedInUserid = useSelector((state: RootState) => state.auth.userid);
  const navigate = useNavigate();
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [sortKey, setSortKey] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<string>("DESC");
  const [visibleHeaderIndex, setVisibleHeaderIndex] = useState<boolean>(false);
  const [advancedSearchData, setAdvancedSearchData] = useState<any>(
    JSON.parse(localStorage.getItem("advancedSearchForm") || "{}")
  );

  const [open, setOpen] = useState<boolean>(false);
  const [action, setAction] = useState<string>("");
  const [selectedUserData, setSelectedUserData] = useState<any>(null);
  const [caseInfoKey, setcaseInfoKey] = useState<string>("0");
  const { InterVal, actionType } = useSelector(
    (state: RootState) => state.DashBoardStage
  );

  // Dispatch open state for form
  useEffect(() => {
    dispatch(setIsSearchEditable(open));
  }, [open, dispatch]);

  const formattedDateTime = (date: string | Date): string | null => {
    if (!date) return null;
    return moment(date).format("MM/DD/YYYY");
  };

  const advancesearchcase = async (
    pageNumber: number = currentPage,
    pageSize: number = itemsPerPage,
    sortColumn: string = sortKey,
    sortOrder: string = sortDirection
  ) => {
    setLoading(true);
    try {
      const response = await advanceSearchCaseList({
        pageNumber,
        pageSize,
        sortColumn,
        sortOrder,
        triggerById: loggedInUserid,
        caseStageIds: actionType != null ? String(actionType) : null,
      });

      if (response?.length > 0) {
        const formattedList = response.map((item: any) => ({
          ...item,
          dateOfExam: formattedDateTime(item.dateOfExam),
        }));

        setFilteredData(formattedList);
        setTotalPages(response[0].totalPages || 1);
        setTotalRows(response[0].totalCount || 0);
      } else {
        setFilteredData([]);
        setTotalPages(1);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error fetching case list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    advancesearchcase();
  }, [currentPage, itemsPerPage, sortKey, sortDirection, advancedSearchData]);

  useEffect(() => {
    setCurrentPage(1); // reset to page 1 when search data changes
  }, [advancedSearchData]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("advancedSearchForm");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    let previousData = JSON.stringify(advancedSearchData);
    const interval = setInterval(() => {
      const currentData = localStorage.getItem("advancedSearchForm") || "{}";
      if (previousData !== currentData) {
        setAdvancedSearchData(JSON.parse(currentData));
        previousData = currentData;
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const handlePageChange = (newPage: number) => setCurrentPage(newPage);
  const handleRowsPerPageChange = (newItemsPerPage: number) =>
    setItemsPerPage(newItemsPerPage);

  const handleSort = (newSortKey: { key: string; direction: string }) => {
    setSortKey(newSortKey.key);
    setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
  };

  const handleRowAction = (row: any, action: string) => {
    dispatch(setIsDashboard(true));
    dispatch(setNewCaseInFoID(row.caseInfoId));
    setVisibleHeaderIndex(true);

    navigate("/search-case");
  };

  const entriesText = !totalRows
    ? "Showing 0 of 0 entries"
    : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(
        currentPage * itemsPerPage,
        totalRows
      )} of ${totalRows} entries`;

  return (
    <div>
      <Loader isLoading={loading} />
      <div className="main_wrapper_section">
        <Container fluid className="px-4">
          <Form className="pb-3">
            <AccordionComponent
              items={[
                {
                  title: "Result",
                  content: (
                    <>
                      <DataTable
                        headers={SEARCHCASE_HEADERS}
                        rows={filteredData}
                        onAction={handleRowAction}
                        onSortAction={handleSort}
                      />
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="col-md-12">
                          <div className="row">
                            <div className="col-md-3 showingData_wrapper">
                              <span>{entriesText}</span>
                            </div>
                            <div className="col-md-9">
                              <div className="d-flex flex-row-reverse">
                                <CustomPagination
                                  currentPage={currentPage}
                                  totalPages={totalPages}
                                  onPageChange={handlePageChange}
                                />
                                <TableRowsPerPage
                                  value={itemsPerPage}
                                  onSelect={handleRowsPerPageChange}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ),
                },
              ]}
              activeKey={caseInfoKey}
              onSelect={(key) => setcaseInfoKey(key as string)}
            />
          </Form>
        </Container>
      </div>
    </div>
  );
};

export default AdvanceSearchCase;
